import React, { useState, useEffect } from "react";
import { Button } from '../components/ui/button'; 
import { useAuth } from "../contexts/AuthContext";
import { CheckCircle, XCircle, Download, Eye } from "lucide-react";
import { getEmployees, getEmployeeById, updateOfferStatus, viewOfferLetter } from '../api/employees';
import { useToast } from "../hooks/useToast";

interface Employee {
  id: string;
  user_id: string;
  employeeID: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  designation?: string;
  department?: string;
  offer_status?: "Pending" | "Accepted" | "Rejected" | string;
  offer_valid_till?: string;
  [key: string]: any; // For other properties
  terms_and_conditions?: string; // Add terms_and_conditions to the Employee interface
}

interface OfferLetterCardProps {
  employee: Employee;
  onUpdateStatus: (id: string, status: string) => Promise<void>;
  onDownload: (id: string, tc_id?: string) => Promise<void>;
  onView: (id: string, tc_id?: string) => Promise<void>;
}

/**
 * OfferLetterCard
 * Displays a card with employee name/role and interactive actions
 * based on the current offer_status.
 */
const OfferLetterCard: React.FC<OfferLetterCardProps> = ({ 
  employee, 
  onUpdateStatus, 
  onDownload, 
  onView 
}) => {
  const { toast } = useToast();

  // Log the employee object to see what we're getting
  console.log("OfferLetterCard received employee:", employee);
  console.log("Employee ID in card:", employee?.id);
  console.log("Offer Status:", employee?.offer_status);

  const handleUpdateStatus = async (id: string, status: string) => {
    try {
      console.log("Updating status with ID:", id);
      await onUpdateStatus(id, status);
      toast({
        title: "Success",
        description: `Offer letter ${status} successfully!`,
        variant: "default",
      });
    } catch (error: any) {
      console.error('Error updating status:', error); 
      toast({
        // title: "Error",
        description: error.message || error.detail|| "Failed to update status",
        variant: "destructive",
      });
    }
  };

  const handleDownload = async (id: string, termsAndCondition?: string) => {
    try {
      console.log("Downloading with ID:", id);
      await onDownload(id,termsAndCondition);
      toast({
        title: "Success",
        description: "Offer letter downloaded successfully!",
        variant: "default",
      });
    } catch (error: any) {
      console.error('Error downloading offer letter:', error);
      toast({
        // title: "Error",
        description: error.message || error.detail|| "Failed to download offer letter",
        variant: "destructive",
      });
    }
  };

  const handleView = async (id: string) => {
    try {
      console.log("Viewing with ID:", id);
      await onView(id);
      toast({
        title: "Success",
        description: "Offer letter opened successfully!",
        variant: "default",
      });
    } catch (error: any) {
      console.error('Error viewing offer letter:', error);
      toast({
        // title: "Error",
        description: error.message || error.detail|| "Failed to view offer letter",
        variant: "destructive",
      });
    }
  };

  // If employee data is not available yet
  if (!employee) {
    return (
      <div className="border border-gray-200 p-4 rounded-lg bg-white shadow-sm">
        <p className="text-gray-500">Loading employee data...</p>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 p-4 rounded-lg flex items-center bg-white shadow-sm hover:shadow-md transition-shadow">
      {/* Left section: employee info */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-semibold">
          {employee.first_name?.charAt(0) || employee.employeeID?.charAt(0) || "E"}
        </div>
        <div>
          <span className="font-medium text-gray-800 block">
            {employee.first_name} {employee.last_name}
          </span>
          <span className="text-sm text-gray-500">
            {employee.designation || "Position"} · {employee.department || "Dept"}
          </span>
          <span className="text-xs text-gray-400 block">
            Employee ID: {employee.id || "MISSING"} 
          </span>
        </div>
      </div>

      {/* Right section: actions / status */}
      <div className="ml-auto flex gap-2 items-center">
        {/* Always show View button for testing */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            console.log("View button clicked, employee ID:", employee.id);
            handleView(employee.id);
          }}
          className="flex items-center gap-1"
        >
          <Eye className="h-4 w-4" />
          View Offer Letter
        </Button>

        {/* PENDING → Show Accept / Reject */}
        {employee.offer_status === "Pending" && (
          <>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-1"
              onClick={() => handleUpdateStatus(employee.id, "Accepted")}
            >
              <CheckCircle className="h-4 w-4" />
              Accept
            </Button>

            <Button
              variant="destructive"
              className="flex items-center gap-1"
              onClick={() => handleUpdateStatus(employee.id, "Rejected")}
            >
              <XCircle className="h-4 w-4" />
              Reject
            </Button>
          </>
        )}

        {/* ACCEPTED → Show Download */}
        {employee.offer_status === "Accepted" && (
          <Button
            className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-1"
            onClick={() => handleDownload(employee.id,  employee.terms_and_conditions)}
          >
            <Download className="h-4 w-4" />
            Download Offer Letter
          </Button>
        )}

        {/* REJECTED → Show message */}
        {employee.offer_status === "Rejected" && (
          <span className="text-red-500 font-medium flex items-center gap-1 bg-red-50 px-3 py-2 rounded-md">
            <XCircle className="h-4 w-4" />
            Offer Rejected
          </span>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------
// Parent component
// ---------------------------------------------------------------------
export default function OfferLetterList() {
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        setLoading(true);
        
        if (!user) {
          console.log("No user found");
          setLoading(false);
          return;
        }

        console.log("Current user from auth:", user);
        
        // Step 1: Get all employees to find the matching employee ID
        const response = await getEmployees();
        const allEmployees = response.employees || [];
        
        console.log("All employees from API:", allEmployees);
        
        // Step 2: Find the employee where user_id matches the logged-in user's ID
        const currentEmployee = allEmployees.find((emp: any) => emp.user_id === user.id);
        
        console.log("Found matching employee:", currentEmployee);
        
        if (currentEmployee && currentEmployee.id) {
          // Step 3: Use the employee ID to fetch complete details via getEmployeeById
          const employeeId = currentEmployee.id;
          console.log("Using employee ID to fetch details:", employeeId);
          
          const detailsResponse = await getEmployeeById(employeeId);
          console.log("Employee details response:", detailsResponse);
          
          // Navigate through the nested structure to get the actual employee object
          if (detailsResponse && 
              detailsResponse.employee && 
              detailsResponse.employee.employee) {
            
            // Extract the actual employee object from the nested structure
            const actualEmployee = detailsResponse.employee.employee as Employee;
            setEmployee(actualEmployee);
            console.log("Employee set successfully:", actualEmployee);
            console.log("Employee ID after setting:", actualEmployee.id);
            
            toast({
              title: "Success",
              description: "Employee data loaded successfully",
              variant: "default",
            });
            
          } else if (detailsResponse && detailsResponse.employee && detailsResponse.employee.id) {
            // Alternative structure: { employee: { id: '...', ... } }
            setEmployee(detailsResponse.employee as Employee);
            console.log("Employee set from detailsResponse.employee:", detailsResponse.employee);
            
            toast({
              title: "Success",
              description: "Employee data loaded successfully",
              variant: "default",
            });
            
          } else if (detailsResponse && detailsResponse.id) {
            // Direct employee object
            setEmployee(detailsResponse as Employee);
            console.log("Employee set directly from detailsResponse:", detailsResponse);
            
            toast({
              title: "Success",
              description: "Employee data loaded successfully",
              variant: "default",
            });
            
          } else {
            console.error("Unexpected response structure:", detailsResponse);
            setError("Employee details not found - unexpected response structure");
            toast({
              // title: "Error",
              description: "Unexpected response structure from server",
              variant: "destructive",
            });
          }
        } else {
          setError("No employee record found for this user");
          toast({
            // title: "Error",
            description: "No employee record found for this user",
            variant: "destructive",
          });
        }
      } catch (error: any) {
        console.error("Employee fetch failed", error);
        setError(error.message || error.detail || "Failed to fetch employee data");
        toast({
          // title: "Error",
          description: error.message || error.detail || "Failed to fetch employee data",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeData();
  }, [user, toast]);

  // Update offer status
  const handleUpdateStatus = async (id: string, status: string) => {
    try {
      console.log(`Updating status for employee ${id} to ${status}`);
      if (!id) {
        console.error("No employee ID provided");
        toast({
          // title: "Error",
          description: "Cannot update status: No employee ID available",
          variant: "destructive",
        });
        return;
      }
      if (!status) {
        console.error("No status provided");
        toast({
          // title: "Error",
          description: "Cannot update status: No status provided",
          variant: "destructive",
        });
        return;
      }
      
      const response = await updateOfferStatus(id, status);
      
      // Update local state to reflect the change
      setEmployee(prevEmployee => 
        prevEmployee && prevEmployee.id === id 
          ? { ...prevEmployee, offer_status: status } 
          : prevEmployee
      );
  
      console.log(`Status updated to ${status}`, response);
      toast({
        title: "Success",
        description: `Offer letter ${status} successfully!`,
        variant: "default",
      });
      
    } catch (err: any) {
      console.error('Error updating status:', err);
      toast({
        // title: "Error",
        description: err.message || err.detail || "Failed to update status",
        variant: "destructive",
      });
    }
  };

  // Download offer letter
  const handleDownload = async (id: string) => {
    try {
      console.log(`Downloading offer letter for employee:`, id);
      if (!id) {
        console.error("No employee ID provided");
        toast({
          // title: "Error",
          description: "Cannot download: No employee ID available",
          variant: "destructive",
        });
        return;
      }
      await viewOfferLetter(id);
      toast({
        title: "Success",
        description: "Offer letter downloaded successfully!",
        variant: "default",
      });
    } catch (err: any) {
      console.error('Error downloading offer letter:', err);
      toast({
        // title: "Error",
        description: err.message || err.detail || "Failed to download offer letter",
        variant: "destructive",
      });
    }
  };

  // View offer letter
  const handleView = async (employeeId: string, termsAndCondition?: string) => {
    try {
      console.log(`Viewing offer letter for employee:`, employeeId);
      if (!employeeId) {
        console.error("No employee ID provided");
        toast({
          // title: "Error",
          description: "Cannot view: No employee ID available",
          variant: "destructive",
        });
        return;
      }
      await viewOfferLetter(employeeId, termsAndCondition || "");
      toast({
        title: "Success",
        description: "Offer letter opened successfully!",
        variant: "default",
      });
    } catch (err: any) {
      console.error('Error viewing offer letter:', err);
      toast({
        // title: "Error",
        description: err.message || err.detail || "Failed to view offer letter",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-6 bg-gray-50 min-h-screen">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto p-6 bg-gray-50 min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4 bg-gray-50 min-h-screen">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">
        Offer Letter Status
      </h2>

      {!employee ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500">No employee data found</p>
        </div>
      ) : (
        <OfferLetterCard 
          key={employee.id} 
          employee={employee}
          onUpdateStatus={handleUpdateStatus}
          onDownload={handleDownload}
          onView={handleView}
        />
      )}
    </div>
  );
}